import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import astroApi from '../api/astro';
import FlexiblePlot from '../components/charts/FlexiblePlot';
import TimeSeriesPlot from '../components/charts/TimeSeriesPlot';

const ObjectDetail = () => {
  const { schemaTable, idColumn, idValue, ra, dec } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [objectData, setObjectData] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [loadingTimeSeries, setLoadingTimeSeries] = useState(false);
  const [searchInfo, setSearchInfo] = useState(null); // For coordinate search info
  const [dataSearch, setDataSearch] = useState('');
  const navigate = useNavigate();

  // Determine if this is a coordinate search or ID search
  const isCoordinateSearch = ra !== undefined && dec !== undefined;

  // Function to load time series data
  const loadTimeSeriesData = async () => {
    if (!schemaTable) return;

    // For coordinate search, we need to get the object ID from objectData
    const objectId = isCoordinateSearch
      ? (objectData && metadata ? objectData[metadata.id_column] : null)
      : idValue;

    if (!objectId) return;

    try {
      setLoadingTimeSeries(true);
      const response = await astroApi.getTimeSeriesData(schemaTable, objectId);
      setTimeSeriesData(response.timeSeriesData || []);
    } catch (err) {
      console.error('Error fetching time series data:', err);
      // We don't set an error state for time series data - just show empty state
    } finally {
      setLoadingTimeSeries(false);
    }
  };

  useEffect(() => {
    const loadObjectData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if we already have the data from navigation state
        if (location.state?.objectData && location.state?.metadata) {
          setObjectData(location.state.objectData);
          setMetadata(location.state.metadata);
          setLoading(false);
          return;
        }

        // Fetch from API based on search type
        let response;
        if (isCoordinateSearch) {
          // Coordinate search
          const radius = location.state?.radius || 0.005;
          response = await astroApi.getObjectByCoordinates(schemaTable, parseFloat(ra), parseFloat(dec), radius);
          console.log('📥 [ObjectDetail] Coordinate Search Response:', response);
          setSearchInfo({
            type: 'coordinates',
            ra: parseFloat(ra),
            dec: parseFloat(dec),
            radius: response.search_radius,
            distance: response.angular_distance
          });
        } else {
          // ID search
          response = await astroApi.getObjectDetail(schemaTable, idColumn, idValue);
          console.log('📥 [ObjectDetail] ID Search Response:', response);
          setSearchInfo({
            type: 'id',
            idColumn,
            idValue
          });
        }

        console.log('  - object:', response.object);
        console.log('  - metadata:', response.metadata);
        console.log('  - metadata.plotting_instructions:', response.metadata?.plotting_instructions);
        setObjectData(response.object);
        setMetadata(response.metadata);
      } catch (err) {
        console.error('Error fetching object details:', err);
        setError(err.response?.data?.detail || 'An error occurred while fetching object details');
      } finally {
        setLoading(false);
      }
    };

    loadObjectData();
  }, [schemaTable, idColumn, idValue, ra, dec, location.state, isCoordinateSearch]);

  // Load time series data when object data is loaded
  useEffect(() => {
    if (objectData && metadata) {
      loadTimeSeriesData();
    }
  }, [objectData, metadata]);
  
  // Get the important columns for display
  const getImportantColumns = () => {
    if (!objectData || !metadata) return [];
    
    return metadata.important_columns.filter(col => 
      objectData[col] !== undefined && objectData[col] !== null && !isNaN(objectData[col])
    );
  };
  
  const getImageUrl = () => {
    // Otherwise generate one from RA/Dec if available
    if (objectData && metadata && 
        objectData[metadata.ra_column] !== undefined && 
        objectData[metadata.dec_column] !== undefined) {
      return `https://www.legacysurvey.org/viewer/cutout.jpg?ra=${objectData[metadata.ra_column]}&dec=${objectData[metadata.dec_column]}&layer=ls-dr10&pixscale=1.00`;
    }
    
    // Fallback to placeholder
    return `https://placehold.co/600x400?text=Astronomical+Object+${idValue}`;
  };
  
  if (loading) {
    return (
      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex justify-center">
          <svg className="animate-spin h-10 w-10 text-primary-600 dark:text-primary-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <p className="text-center text-ui-light-text-secondary dark:text-ui-dark-text-secondary mt-2">Loading object details...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
          <strong className="font-bold">Error: </strong>
          <span>{error}</span>
        </div>
        <div className="text-center mt-4">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 dark:bg-primary-500 rounded-md hover:bg-primary-700 dark:hover:bg-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  if (!objectData || !metadata) {
    return (
      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-400 dark:border-yellow-800 text-yellow-700 dark:text-yellow-400 px-4 py-3 rounded mb-4">
          <span>No data available for this object.</span>
        </div>
        <div className="text-center mt-4">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 dark:bg-primary-500 rounded-md hover:bg-primary-700 dark:hover:bg-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div className="mb-6">
        <div className="flex items-center mb-2">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
          >
            ← Back
          </button>
          <h1 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">
            {metadata.display_name || schemaTable} Object Detail
          </h1>
        </div>

        {/* Show search info */}
        {searchInfo && (
          <div className="ml-14 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
            {searchInfo.type === 'coordinates' ? (
              <span>
                Found by coordinates: RA={searchInfo.ra.toFixed(6)}°, Dec={searchInfo.dec.toFixed(6)}°
                {searchInfo.distance && (
                  <span className="ml-2 text-primary-600 dark:text-primary-400">
                    (distance: {(searchInfo.distance * 3600).toFixed(2)}" = {searchInfo.distance.toFixed(6)}°)
                  </span>
                )}
              </span>
            ) : (
              <span>
                Found by ID: {searchInfo.idColumn} = {searchInfo.idValue}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Image section - Takes 1/3 of the width on large screens */}
        <div className="bg-ui-light-card dark:bg-ui-dark-card shadow-md rounded-lg p-6 border border-ui-light-border dark:border-ui-dark-border">
          <h2 className="text-xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary mb-4">Object Image</h2>
          <div className="flex justify-center">
            <img 
              src={getImageUrl()} 
              alt={`Astronomical object ${idValue}`}
              className="max-w-full h-auto rounded-md"
            />
          </div>
          <p className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary mt-2 text-center">
            Position: RA {typeof objectData[metadata.ra_column] === 'number' ? 
                objectData[metadata.ra_column].toFixed(6) : objectData[metadata.ra_column]}, 
            Dec {typeof objectData[metadata.dec_column] === 'number' ? 
                objectData[metadata.dec_column].toFixed(6) : objectData[metadata.dec_column]}
          </p>
          <div className="mt-4 text-center">
            <a
              href={`https://www.legacysurvey.org/viewer?ra=${objectData[metadata.ra_column]}&dec=${objectData[metadata.dec_column]}&zoom=15`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline transition-colors"
            >
              View in Legacy Survey Viewer
            </a>
          </div>
        </div>
        
        {/* Data Visualization - Takes 2/3 of the width on large screens */}
        <div className="lg:col-span-2">
          <FlexiblePlot objectData={objectData} metadata={metadata} />
        </div>
        
        {/* Time Series section - Hidden for now 
        <div className="lg:col-span-3">
          <TimeSeriesPlot 
            timeSeriesData={timeSeriesData}
            objectId={idValue}
            title="Light Curve Data"
            loading={loadingTimeSeries}
          />
        </div>
        */}
      </div>
      
      {/* Data section */}
      <div className="bg-ui-light-card dark:bg-ui-dark-card shadow-md rounded-lg p-6 border border-ui-light-border dark:border-ui-dark-border">
        <div className="flex items-center justify-between gap-4 mb-5">
          <h2 className="text-xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">
            All Object Data
          </h2>
          <input
            type="text"
            placeholder="Search properties…"
            value={dataSearch}
            onChange={e => setDataSearch(e.target.value)}
            className="w-52 px-3 py-1.5 text-sm border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {(() => {
          const importantCols = new Set(metadata.important_columns || []);
          const allEntries = Object.entries(objectData).filter(([k]) =>
            !dataSearch || k.toLowerCase().includes(dataSearch.toLowerCase())
          );
          const keyEntries = allEntries.filter(([k]) => importantCols.has(k));
          const restEntries = allEntries.filter(([k]) => !importantCols.has(k));

          const renderCard = (key, value, highlight) => {
            const isRaDec = key === metadata.ra_column || key === metadata.dec_column;
            const display = value !== null
              ? (isRaDec && typeof value === 'number' ? value.toFixed(6) : String(value))
              : '—';
            return (
              <div
                key={key}
                className={`px-3 py-2 rounded-lg border ${
                  highlight
                    ? 'border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/15'
                    : 'border-ui-light-border dark:border-ui-dark-border bg-ui-light-background dark:bg-ui-dark-background'
                }`}
              >
                <div className={`text-xs font-medium truncate mb-0.5 ${
                  highlight
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary'
                }`} title={key}>
                  {key}
                </div>
                <div className="text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary truncate font-mono" title={display}>
                  {display}
                </div>
              </div>
            );
          };

          return (
            <div className="space-y-5">
              {keyEntries.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider mb-2">
                    Key properties
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-2">
                    {keyEntries.map(([k, v]) => renderCard(k, v, true))}
                  </div>
                </div>
              )}

              {restEntries.length > 0 && (
                <div>
                  {keyEntries.length > 0 && (
                    <p className="text-xs font-semibold text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider mb-2">
                      All properties
                    </p>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-2">
                    {restEntries.map(([k, v]) => renderCard(k, v, false))}
                  </div>
                </div>
              )}

              {allEntries.length === 0 && (
                <p className="text-sm text-center text-ui-light-text-secondary dark:text-ui-dark-text-secondary py-4">
                  No properties match "{dataSearch}"
                </p>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default ObjectDetail;